/**
 * Firestore implementation of PurchaseOrderRepository.
 *
 * Path: purchaseOrders/{id}
 *
 * Purchase orders — vendor-facing commitments to buy at agreed prices,
 * promoted from an APPROVED requisition or created standalone. See
 * src/types/purchaseOrder.ts.
 */

import {
  collection,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import type { PurchaseOrder, EntityId } from '../../../types';
import type { PurchaseOrderRepository } from '../types';

const COLLECTION = 'purchaseOrders';

export const firestorePurchaseOrderRepository: PurchaseOrderRepository = {
  async findById(id: EntityId): Promise<PurchaseOrder | null> {
    const snap = await getDoc(doc(db, COLLECTION, id));
    return snap.exists() ? (snap.data() as PurchaseOrder) : null;
  },

  async save(entity: PurchaseOrder): Promise<void> {
    await setDoc(doc(db, COLLECTION, entity.id), entity);
  },

  async delete(id: EntityId): Promise<void> {
    await deleteDoc(doc(db, COLLECTION, id));
  },

  async list(): Promise<PurchaseOrder[]> {
    const snap = await getDocs(collection(db, COLLECTION));
    return snap.docs.map((d) => d.data() as PurchaseOrder);
  },

  async findByOrder(orderId: EntityId): Promise<PurchaseOrder[]> {
    // Single-field query on `orderId`. The dual-field (`projectId` OR `orderId`)
    // migration read was retired 2026-09-04 (backlog #80): prod carried zero
    // `projectId`-only docs and the extra query ran on every order-detail visit.
    const snap = await getDocs(query(collection(db, COLLECTION), where('orderId', '==', orderId)));
    return snap.docs.map((d) => d.data() as PurchaseOrder);
  },
};
